import React, { FormEvent, useEffect, useState, useRef } from "react";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import {
    DA_Title,
    DA_TitleTag,
} from "@danishagro/shared/src/components/atoms/Title/Title.component";
import { Receipt } from "@danishagro/cms/src/blocks/components/TextForm/components/Receipt/Receipt.component";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import { getAbsoluteUrl } from "@helpers/getAbsoluteUrl.helper";
import { GET_CMS_SITE_ORIGIN } from "@danishagro/shared/src/helpers/urlRoot.helper";
import { pushToDataLayer } from "@danishagro/shared/src/gtm/useTagManger";
import { DA_Spinner } from "@danishagro/shared/src/components/atoms/Spinner/Spinner.component";
import { DynamicField } from "./components/DynamicField.component";
import { FormProps } from "./Form.interface";
import { useValidation } from "./hooks/useValidation.hook";
import { useFileUpload } from "./hooks/useFileUpload.hook";
import S from "./Form.module.scss";

export const Form = (props: FormProps) => {
    const { errorMessages, validate, addErrors, getError, onInputChange } = useValidation(
        props.formData
    );
    const { checkForFileFields, uploadFile } = useFileUpload();
    const [showValidationSummary, setValidationSummary] = useState(false);
    const [showReceiptModule, setShowReceiptModule] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const [validationErrorTitle, setValidationErrorTitle] = useState("");

    const [viewState, setViewState] = useState("INIT");

    const { scrollTo } = useScreen();

    const [submitted, setSubmitted] = useState(false);

    // Helper function to determine if a field is a checkbox
    function isCheckboxField(key: string): boolean {
        // Implement logic to determine if a key belongs to a checkbox field
        // For example, based on the fieldTypeId or structure of your form fields
        return props.formData.allFieldSets.some((fieldset) =>
            fieldset.containers.some((container) =>
                container.fields.some(
                    (field) =>
                        field.fieldTypeId === "fab43f20-a6bf-11de-a28f-9b5755d89593" &&
                        field.alias === key
                )
            )
        );
    }

    const onSubmit = async (event: FormEvent) => {
        event?.preventDefault();

        const form = new FormData(event?.target as HTMLFormElement);

        const body = {} as FormProps;
        const datePattern = /^\d{2}\.\d{2}\.\d{4}$/;
        // Temporary storage for checkbox values
        const checkboxValues: { [key: string]: string[] } = {};

        form.forEach((value, key) => {
            // Date picker
            if (typeof value === "string" && datePattern.test(value)) {
                const [dd, mm, yyyy] = (value as string).split(".");
                body[key] = `${yyyy}/${mm}/${dd}`;
            }
            // File picker
            else if (value instanceof File) {
                if (value !== null && (value as File).name !== "") {
                    body[key] = value;
                } else {
                    body[key] = "";
                }
            } // Special handling for checkboxes
            else if (isCheckboxField(key)) {
                // Initialize array for checkbox values if not already done
                if (!checkboxValues[key]) {
                    checkboxValues[key] = [];
                }
                // Aggregate values for checkbox fields
                checkboxValues[key].push(value);
            }
            // Handling for other inputs
            else {
                body[key] = value;
            }
        });

        // Add aggregated checkbox values to the body
        for (const key in checkboxValues) {
            body[key] = checkboxValues[key];
        }

        // 1. Validation
        const [isValid] = validate(body);

        if (!isValid) {
            return;
        }

        setViewState("SUBMITTING");

        // 2. Check for file fields
        const { hasFile, fileFields } = checkForFileFields(body);

        if (hasFile) {
            // TODO: Show a uploading loading indicator
            // TODO: Show an error and stop the form from submitting

            await Promise.all(
                fileFields.map(async (field) => {
                    const [didUpload, fileUrl] = await uploadFile(body?.[field]);

                    if (didUpload && field) {
                        body[field] = fileUrl;

                        // TODO: Show a uploading success indicator
                    } else {
                        console.log("Error uploading file");
                    }
                })
            );
        }

        // 3. Submit form
        const url = `${GET_CMS_SITE_ORIGIN(
            "ABSOLUTE"
        )}/submitform?url=/umbraco/forms/api/v1/entries/${props.formData.id}`;

        const request = await fetch(getAbsoluteUrl(url, "SPA"), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Api-Key": "88291c19-c109-p!c0-4227-97d2-3e95b7084b92",
            },
            body: JSON.stringify({ values: body }),
        });

        if (request.ok) {
            const response = await request.json();

            if (response?.statusCode === 422) {
                console.log("Der skete en fejl");
            } else {
                setSubmitted(true);
                // GTM Tracking
                // TODO: add form id - pushToDataLayer
                pushToDataLayer({
                    event: props?.formData.name || "form",
                });
                setShowReceiptModule(true);
            }
        } else if (request.status === 422) {
            const response = await request.json();
            addErrors(response.errors);
            setValidationErrorTitle(response.title);
        }

        setViewState("INIT");
        setValidationSummary(true);
    };

    useEffect(() => {
        if (submitted) {
            scrollTo(containerRef.current.offsetParent);
            setShowReceiptModule(true);

            const timer = setTimeout(() => {
                setSubmitted(false);
                setShowReceiptModule(false);
            }, 15000);

            return () => clearTimeout(timer);
        }
    }, [submitted, scrollTo]);

    return (
        <div className={S.form} ref={containerRef}>
            {/** Form Header */}
            {!showReceiptModule && (props.formTagline || props.formHeadline) ? (
                <div className={S.textWrapper}>
                    {/** Form Tagline */}
                    {props.formTagline ? <span>{props.formTagline}</span> : null}

                    {/** Form Headline */}
                    {props.formHeadline ? (
                        <DA_Title h3 tag={DA_TitleTag.H3} noMargin fullWidth>
                            {props.formHeadline}
                        </DA_Title>
                    ) : null}
                </div>
            ) : null}

            {/** Form */}

            {!showReceiptModule ? (
                <form onSubmit={onSubmit} noValidate>
                    <fieldset className={S.fieldset} disabled={viewState === "SUBMITTING"}>
                        {/** Validation summary */}
                        {showValidationSummary && (
                            <>
                                {props.formData.showValidationSummary === true ? (
                                    <ul className={S.summaryList}>
                                        {errorMessages && (
                                            <li className={S.summaryItem}>
                                                <h4>{validationErrorTitle}</h4>
                                            </li>
                                        )}

                                        {errorMessages?.map((error, key) => {
                                            return (
                                                <li key={key} className={S.summaryItem}>
                                                    {error.alias}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ) : null}
                            </>
                        )}

                        {/** Groups */}

                        {props?.formData?.allFieldSets?.map((fieldset, index) => (
                            <div className={S.group} key={index}>
                                {/** Containers */}
                                {fieldset.containers?.map((container, index) => {
                                    const containerSizeClass = S?.[`size_${container.width}`];

                                    return (
                                        <div
                                            key={index}
                                            className={cn(S.container, containerSizeClass)}
                                        >
                                            {/** Fields */}

                                            {container?.fields?.map((field, index) => (
                                                <DynamicField
                                                    key={index}
                                                    getError={getError}
                                                    onInputChange={onInputChange}
                                                    field={field}
                                                    formData={props.formData}
                                                />
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}

                        {/** Submit */}
                        <div>
                            {viewState === "SUBMITTING" && <DA_Spinner className={S.spinner} />}

                            <DA_Button
                                title={props.formData.submitLabel}
                                submit
                                disabled={viewState === "SUBMITTING"}
                            />
                        </div>
                    </fieldset>
                </form>
            ) : null}

            {/** Receipt */}
            {showReceiptModule ? <Receipt {...props.receipt} /> : null}
        </div>
    );
};

export default Form;
